import React from "react";
import ProfileImage from "./ProfileImage";
import { Delete, Edit, Pencil, Trash2, Eye } from "lucide-react";
import Icon from "components/AppIcon";

const DataTable = ({
  columns,
  data,
  isLoading,
  error,
  handleSwitch,
  sortConfig,
  onSort,
  onRetry,
  emptyMessage,
  currentPage,
  itemsPerPage,
  onAction,
  selectedItems = [],
  onSelectAll,
  onSelectItem,
}) => {

  const getStatusBadge = (status) => {
    const statusConfig = {
      sent: { color: "bg-green-100 text-green-800", label: "Sent" },
      failed: { color: "bg-red-100 text-red-800", label: "Failed" },
      pending: { color: "bg-yellow-100 text-yellow-800", label: "Pending" }
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const renderHeader = () => (
    <thead className="bg-gray-50 whitespace-nowrap">
      <tr>
        {columns.map((col) => {
          if (col.key === "select") {
            return (
              <th key={col.key} className="px-6 py-3 text-left w-12">
                <input
                  type="checkbox"
                  checked={selectedItems.length === data.length && data.length > 0}
                  onChange={onSelectAll}
                  className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                />
              </th>
            );
          }

          return (
            <th
              key={col.key}
              className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${col.sortable ? "cursor-pointer select-none" : ""
                }`}
              onClick={() => col.sortable && onSort(col.key)}
            >
              {col.label}
            </th>
          );
        })}
      </tr>
    </thead>
  );

  const renderRows = () => (
    <tbody className="bg-white divide-y divide-gray-200 whitespace-nowrap">
      {data.map((item, index) => (
        <tr key={index} className="hover:bg-gray-50">
          {columns.map((col) => {
            let value;

            if (col.key === "select") {
              value = (
                <input
                  type="checkbox"
                  checked={selectedItems.includes(item._id)}
                  onChange={() => onSelectItem(item._id)}
                  className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                />
              );
            } else if (col.key === "sno") {
              value = index + 1 + (currentPage - 1) * itemsPerPage;
            } else if (col.key === "to") {
              const emails = item[col.key];
              if (Array.isArray(emails)) {
                value = emails.length > 1
                  ? `${emails[0]}, ...`
                  : emails[0] || "-";
              } else {
                value = emails || "-";
              }
            } else if (col.key === "createdAt" || col.key === "sentAt") {
              value = item[col.key] ? new Date(item[col.key]).toLocaleDateString() : "-";
            } else if (col.key === "description" || col.key === "shortDescription") {
              const fullText = item[col.key] ?? "";
              const words = fullText.split(" ");
              const preview = words.length > 10 ? words.slice(0, 10).join(" ") + "..." : fullText;
              value = (
                <span
                  className="cursor-pointer text-primary-600 underline"
                  onClick={() => onAction(item, "view")}
                  dangerouslySetInnerHTML={{ __html: preview }}
                />
              );
            } else if (col.key === "status") {
              value = getStatusBadge(item[col.key] || "pending");
            } else if (col.key === 'isActive') {
              value = (
                <div className="flex items-center whitespace-nowrap space-x-3 sm:order-1 order-2">
                  <button
                    onClick={() => handleSwitch(item, !item[col.key])}
                    className={`w-10 h-5 flex items-center rounded-full p-1 transition-colors duration-200 ${item[col.key] ? 'bg-primary-600' : 'bg-gray-300'
                      }`}
                  >
                    <div
                      className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-transform duration-200 ${item[col.key] ? 'translate-x-5' : 'translate-x-0'
                        }`}
                    />
                  </button>
                </div>
              );
            } else if (col.key === "actions") {
              value = (
                <div className="flex space-x-3">
                  <span className="flex gap-1 text-blue-600 cursor-pointer" onClick={() => onAction(item, "view")}>
                    <Eye className="w-4 h-4" /> View
                  </span>

                </div>
              );
            } else {
              value = item[col.key] ?? "-";
            }

            return (
              <td
                key={col.key}
                className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap"
              >
                {value}
              </td>
            );
          })}
        </tr>
      ))}
    </tbody>
  );

  const renderError = () => (
    <tbody className="whitespace-nowrap">
      <tr>
        <td colSpan={columns.length} className="px-6 py-12 text-center text-sm text-gray-500">
          <div className="flex flex-col items-center">
            <Icon name="AlertCircle" size={32} className="text-error mb-2" />
            <p className="font-medium mb-2">{error}</p>
            <button
              onClick={onRetry}
              className="mt-2 px-3 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700"
            >
              Retry
            </button>
          </div>
        </td>
      </tr>
    </tbody>
  );

  const renderEmpty = () => (
    <tbody className="whitespace-nowrap">
      <tr>
        <td colSpan={columns.length} className="px-6 py-12 text-center text-sm text-gray-500">
          <div className="flex flex-col items-center">
            <Icon name="Search" size={32} className="text-gray-400 mb-2" />
            <p className="font-medium mb-2">{emptyMessage}</p>
          </div>
        </td>
      </tr>
    </tbody>
  );

  const renderLoading = () => (
    <tbody className="bg-white divide-y divide-gray-200 whitespace-nowrap">
      {[...Array(3)].map((_, index) => (
        <tr key={index}>
          {columns.map((_, i) => (
            <td key={i} className="px-6 py-4">
              <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );

  return (
    <div className="!overflow-x-auto">
      <table className="w-full">
        {renderHeader()}
        {isLoading
          ? renderLoading()
          : error
            ? renderError()
            : data.length === 0
              ? renderEmpty()
              : renderRows()}
      </table>
    </div>
  );
};

export default DataTable;